import Tv from "@mui/icons-material/Tv";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";
import { useTranslation } from "react-i18next";

import type { PartyShellPalette } from "../theme/theme";

const CAST_TOGGLE_ICON_PX = 26;

/** TV with a diagonal strike — “single device”, no second screen. */
function TvStrikethroughIcon() {
  return (
    <Box
      component="span"
      sx={{
        position: "relative",
        display: "inline-flex",
        width: CAST_TOGGLE_ICON_PX * 1.2,
        height: CAST_TOGGLE_ICON_PX,
        alignItems: "center",
        justifyContent: "center",
        color: "inherit",
      }}
    >
      <Tv sx={{ fontSize: CAST_TOGGLE_ICON_PX, opacity: 0.92 }} />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          left: "6%",
          right: "6%",
          top: "50%",
          height: 2.5,
          borderRadius: 1,
          bgcolor: "currentColor",
          transform: "rotate(-34deg)",
          opacity: 0.95,
        }}
      />
    </Box>
  );
}

type Props = {
  /** When true, this option is hidden (native TV casting mode). */
  nativeTvSupported: boolean;
  castByQr: boolean;
  onChange: (castByQr: boolean) => void;
  /** MUI party palette from theme */
  partyPal: PartyShellPalette;
};

/**
 * Setup choice: play reveals on this phone, or show a QR URL so another device can open the same content.
 */
const PartyCastSourceToggle: React.FC<Props> = ({
  nativeTvSupported,
  castByQr,
  onChange,
  partyPal,
}) => {
  const { t } = useTranslation();

  if (nativeTvSupported) return null;

  return (
    <Box sx={{ px: 2.5, py: 1.5, mb: 1 }}>
      <Typography className="party-mono" sx={{ fontSize: 11, color: partyPal.inkDim, letterSpacing: "0.12em", mb: 1 }}>
        {t("party.cast.displayTargetLabel")}
      </Typography>
      <Stack direction="row" spacing={2.5}>
        <button
          type="button"
          onClick={() => onChange(false)}
          className="party-btn"
          style={{
            flex: 1,
            borderRadius: 14,
            padding: "10px 8px 12px",
            fontWeight: 800,
            fontSize: 13,
            border: castByQr ? `1.5px solid ${partyPal.inkFaint}` : "none",
            background: !castByQr ? partyPal.hot : "rgba(255,255,255,0.06)",
            color: !castByQr ? "#fff" : partyPal.ink,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <TvStrikethroughIcon />
          <span style={{ lineHeight: 1.25, textAlign: "center" }}>{t("party.cast.thisPhone")}</span>
        </button>
        <button
          type="button"
          onClick={() => onChange(true)}
          className="party-btn"
          style={{
            flex: 1,
            borderRadius: 14,
            padding: "10px 8px 12px",
            fontWeight: 800,
            fontSize: 13,
            border: !castByQr ? `1.5px solid ${partyPal.inkFaint}` : "none",
            background: castByQr ? partyPal.hot : "rgba(255,255,255,0.06)",
            color: castByQr ? "#fff" : partyPal.ink,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          <Tv sx={{ fontSize: CAST_TOGGLE_ICON_PX, opacity: 0.92 }} />
          <span style={{ lineHeight: 1.25, textAlign: "center" }}>{t("party.cast.tvOrOtherPhone")}</span>
        </button>
      </Stack>
    </Box>
  );
};

export default PartyCastSourceToggle;
